import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    getFormResponse, getFormResponseForAttendee, getFormResponseForResponder, getNonAttendeesAndProjects, getResponseListingFilterOptions, getResponseListingFilterOptionsForAttendee, updateFormResponse, getFormResponseForMentor,
    getResponseListingFilterOptionsForMentor
} from "../services/formResponseAPI";
import toast from "react-hot-toast";
import CustomToaster from "../components/Toaster";
import { TOAST_TIME, TOAST_TYPE } from "../constants/constants";


export const fetchFormResponse = createAsyncThunk(
    "formresponse/fetch",
    async ({ formId, status, triggerId, offset = 0 }, { rejectWithValue }) => {
        try {
            const response = await getFormResponse(formId, status, triggerId, offset);
            return response.data;
        }
        catch (error) {
            toast.custom(
                <CustomToaster
                    type={TOAST_TYPE.ERROR}
                    message="Failed to fetch form response."
                    time={TOAST_TIME.FIVE}
                />
            );
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);


export const fetchFormResponseForResponder = createAsyncThunk(
    "formresponse/fetchForResponder",
    async ({ formId, triggerId, status, offset }, { rejectWithValue }) => {
        try {
            const response = await getFormResponseForResponder({
                formId,
                triggerId,
                status,
                offset
            });
            return response.data;
        } catch (error) {
            toast.custom(
                <CustomToaster
                    type={TOAST_TYPE.ERROR}
                    message="Failed to fetch attendee responses."
                    time={TOAST_TIME.FIVE}
                />
            );
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchMyFeedbacks = createAsyncThunk(
    "formresponse/fetchMyFeedbacks",
    async ({ formId, offset }, { rejectWithValue }) => {
        try {
            const response = await getFormResponseForAttendee({
                formId,
                offset
            });
            return response.data;
        } catch (error) {
            toast.custom(
                <CustomToaster
                    type={TOAST_TYPE.ERROR}
                    message="Failed to fetch your feedbacks."
                    time={TOAST_TIME.FIVE}
                />
            );
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);


export const fetchResponseListingFilterOptions = createAsyncThunk(
    "formresponse/fetchFilterOptions",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getResponseListingFilterOptions();
            return response.data;
        } catch (error) {
            toast.custom(
                <CustomToaster
                    type={TOAST_TYPE.ERROR}
                    message="Failed to load filter options."
                    time={TOAST_TIME.FIVE}
                />
            );
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchResponseListingFilterOptionsForAttendee = createAsyncThunk(
    "formresponse/fetchFilterOptions",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getResponseListingFilterOptionsForAttendee();
            return response.data;
        } catch (error) {
            toast.custom(
                <CustomToaster
                    type={TOAST_TYPE.ERROR}
                    message="Failed to load filter options."
                    time={TOAST_TIME.FIVE}
                />
            );
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fillFormResponse = createAsyncThunk(
    "formresponse/fillFormResponse",
    async ({ formDetails }, { rejectWithValue }) => {
        try {
            const response = await updateFormResponse(formDetails);
            return response.data;
        } catch (error) {
            toast.custom(
                <CustomToaster
                    type={TOAST_TYPE.ERROR}
                    message="Failed to fill responses."
                    time={TOAST_TIME.FIVE}
                />
            );
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

export const fetchNonAttendeesAndProjects = createAsyncThunk(
    "formresponse/fetchForNonAttendeesAndProjects",
    async ({ formId }, { rejectWithValue }) => {
        try {
            const response = await getNonAttendeesAndProjects({
                formId
            });
            return response.data;
        } catch (error) {
            toast.custom(
                <CustomToaster
                    type={TOAST_TYPE.ERROR}
                    message="Failed to fetch NonAttendeesAndProjects responses."
                    time={TOAST_TIME.FIVE}
                />
            );
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const fetchMentorFeedbacks = createAsyncThunk(
    "formresponse/fetchMentorFeedbacks",
    async ({ formId, offset }, { rejectWithValue }) => {
        try {

            const response = await getFormResponseForMentor({
                formId,
                offset
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchResponseListingFilterOptionsForMentor = createAsyncThunk(
    "formresponse/fetchMentorFilterOptions",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getResponseListingFilterOptionsForMentor();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);


const formResponseSlice = createSlice({
    name: "formresponse",
    initialState: {
        list: [],
        loading: false,
        error: null,
        totalResults: 0,
        currentPage: 1,
        pageSize: 20,
        filterOptions: {
            form: [],
            bootcamps: [],
            statuses: [],
        },
        nonAttendeesAndProjects: {
            users: [],
            projects: []
        },
    },
    reducers: {
        setPage: (state, action) => {
            state.currentPage = action.payload;
        },
        clearFormResponse: (state) => {
            state.list = [];
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFormResponse.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchFormResponse.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload;
                state.totalResults = action.payload.totalResults || state.list.length;
            })
            .addCase(fetchFormResponse.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchFormResponseForResponder.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchFormResponseForResponder.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload;
                state.totalResults = action.payload.totalResults || state.list.length;
            })
            .addCase(fetchFormResponseForResponder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchResponseListingFilterOptions.fulfilled, (state, action) => {
                state.filterOptions = action.payload;
            })
            .addCase(fillFormResponse.pending, (state) => {
                state.loading = true;
            })
            .addCase(fillFormResponse.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(fillFormResponse.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchNonAttendeesAndProjects.fulfilled, (state, action) => {
                state.nonAttendeesAndProjects = action.payload || {
                    users: [],
                    projects: [],
                };
            })
            .addCase(fetchNonAttendeesAndProjects.rejected, (state) => {
                state.nonAttendeesAndProjects = { users: [], projects: [] };
            }).addCase(fetchMyFeedbacks.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchMyFeedbacks.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload;
                state.totalResults = action.payload.totalResults || state.list.length;
            })
            .addCase(fetchMyFeedbacks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchMentorFeedbacks.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchMentorFeedbacks.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload;
                state.totalResults = action.payload.totalResults || 0;
            })
            .addCase(fetchMentorFeedbacks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
})

export const { setPage, clearFormResponse } = formResponseSlice.actions;
export default formResponseSlice.reducer;